import { Button, Collapse, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { EllipsisOutlined, PlusCircleOutlined } from '@ant-design/icons';
import get from 'lodash/get';

import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import { useEditSession } from '../EditSession/EditSessionContext';
import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import type { CalendarBarFieldEntry } from '../../config/calendarColumnsConfig';
import {
    TPO_EVENTS_ADD_PROMO_BTN,
    TPO_EVENT_PROMO_SECTION,
    TPO_EVENT_PROMO_SECTION_EMPTY,
    tpoEventPromoActionsBtnTestId,
    tpoEventPromoDeleteItemTestId,
    tpoEventPromoEditItemTestId,
    tpoEventPromoRowTestId,
} from '../../config/testIds';
import {
    resolveFieldLabel,
    type FieldValueLabelMaps,
} from '../../utils/resolveFieldLabel';
import type { PromoEntry } from './hooks/useGetEventData';

const COLLAPSE_STYLE = { backgroundColor: '#FFF' } as const;

type PromoActionBarProps = {
    name: string;
    menuItems: MenuProps['items'];
    onClick: () => void;
    promoId: string;
};

const PromoActionBar = ({ name, menuItems, onClick, promoId }: PromoActionBarProps) => {
    return (
        <div className="flex flex-row py-2 px-3 items-center justify-between border border-[#E4E4E7] rounded-lg bg-[#FAFAFA] cursor-pointer hover:bg-[#F4F4F5]"
            onClick={onClick}
            data-testid={tpoEventPromoRowTestId(promoId)}
        >
            <p className="text-sm font-semibold">{name}</p>
            <Dropdown
                menu={{ items: menuItems }}
                trigger={['click']}
                overlayStyle={{
                    width: '10.9375rem',
                    borderRadius: '0.5rem',
                }}
            >
                <span className="cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={tpoEventPromoActionsBtnTestId(promoId)}
                >
                    <EllipsisOutlined />
                </span>
            </Dropdown>
        </div>
    );
};

/**
 * Resolve PromoActionBar label from `promoListLabelField`, mapping raw values
 * through `fieldValueLabelMaps` when `resolveLabelFrom` is set (e.g. mechanic).
 */
const resolvePromoListLabel = (
    promo: PromoEntry,
    labelField: CalendarBarFieldEntry,
    labelMaps: FieldValueLabelMaps,
): string => {
    const raw = get(promo, labelField.field);
    if (raw == null || raw === '') return promo.promo_name || promo.promo_id;
    const resolved = resolveFieldLabel(String(raw), labelMaps, labelField.resolveLabelFrom);
    return resolved || promo.promo_name || promo.promo_id;
};

type EventPromoSectionProps = {
    isEventSaved?: boolean;
    /** Rule-driven disable for the Add-promo button (OR'd with `!isEventSaved`). */
    addPromoDisabled?: boolean;
    promos?: PromoEntry[];
    onAddPromo?: () => void;
    onEditPromo?: () => void;
    onDeletePromo?: (promoId: string) => void;
};

const PromoDetailsEmptyState = ({ isEventSaved, addPromoDisabled, promos = [], onAddPromo, onDeletePromo }: EventPromoSectionProps) => {
    const tpoGenConfig = useTpoGenConfig();
    const { setEditPromoId } = usePlanDetailsContext();
    const { canEdit } = useEditSession();

    const getMenuItems = (promoId: string): MenuProps['items'] => [
        {
            // Opening a promo stays valid read-only, so this relabels rather
            // than disables; deleting does not, so it goes.
            label: canEdit ? tpoGenConfig.labels.common.edit : tpoGenConfig.labels.editSession.viewPromoAction,
            key: 'edit',
            onClick: () => setEditPromoId(promoId),
            'data-testid': tpoEventPromoEditItemTestId(promoId),
        } as NonNullable<MenuProps['items']>[number],
        {
            label: <span className="text-[#FF4D4F]">{tpoGenConfig.labels.common.delete}</span>,
            key: 'delete',
            disabled: !canEdit,
            onClick: () => onDeletePromo?.(promoId),
            'data-testid': tpoEventPromoDeleteItemTestId(promoId),
        } as NonNullable<MenuProps['items']>[number],
    ];

    const isAddPromoDisabled = !canEdit || !isEventSaved || Boolean(addPromoDisabled);

    return (
        <div>
            <Button
                type="primary"
                disabled={isAddPromoDisabled}
                onClick={() => onAddPromo?.()}
                icon={<PlusCircleOutlined />}
                style={{ display: 'flex', alignItems: 'center' }}
                data-testid={TPO_EVENTS_ADD_PROMO_BTN}
                data-state-disabled={isAddPromoDisabled ? 'true' : undefined}
            >
                {tpoGenConfig.labels.promos.addPromo}
            </Button>

            {promos.length === 0 ? (
                <div
                    className="my-8 flex flex-row justify-center items-center"
                    data-testid={TPO_EVENT_PROMO_SECTION_EMPTY}
                >
                    <p className="text-sm text-[#9F9FA9]">{tpoGenConfig.labels.promos.noPromotionsAdded}</p>
                </div>
            ) : (
                <div className="flex flex-col gap-y-2 my-4">
                    {promos.map((promo) => (
                        <PromoActionBar
                            key={promo.promo_id}
                            name={resolvePromoListLabel(
                                promo,
                                tpoGenConfig.promoListLabelField,
                                tpoGenConfig.fieldValueLabelMaps,
                            )}
                            menuItems={getMenuItems(promo.promo_id)}
                            onClick={() => setEditPromoId(promo.promo_id)}
                            promoId={promo.promo_id}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const EventPromoSection = ({
    isEventSaved,
    addPromoDisabled,
    promos,
    onAddPromo,
    onEditPromo,
    onDeletePromo,
}: EventPromoSectionProps) => {
    const tpoGenConfig = useTpoGenConfig();
    return (
        <div data-testid={TPO_EVENT_PROMO_SECTION}>
            <Collapse expandIconPosition="end" defaultActiveKey={['1']} style={COLLAPSE_STYLE}>
                <Collapse.Panel
                    header={<p className="text-base font-semibold">{tpoGenConfig.labels.promos.sectionTitle}</p>}
                    key="1"
                >
                    <PromoDetailsEmptyState
                        isEventSaved={isEventSaved}
                        addPromoDisabled={addPromoDisabled}
                        promos={promos}
                        onAddPromo={onAddPromo}
                        onEditPromo={onEditPromo}
                        onDeletePromo={onDeletePromo}
                    />
                </Collapse.Panel>
            </Collapse>
        </div>
    );
};

export default EventPromoSection;
